'use client';

import { useTranslations } from 'next-intl';
import {
  ArrowUpToLine,
  CheckCircle2,
  Clock,
  Loader2,
  RotateCcw,
  Undo2
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { OcrQaListStatus } from '../types';

const STATUS_STYLE: Record<
  OcrQaListStatus,
  { className: string; icon: typeof Clock }
> = {
  pending: {
    className:
      'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    icon: Clock
  },
  in_progress: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: Loader2
  },
  returned: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icon: RotateCcw
  },
  forwarded: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: ArrowUpToLine
  },
  sent_back: {
    className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
    icon: Undo2
  },
  completed: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: CheckCircle2
  }
};

interface OcrQaListStatusBadgeProps {
  status: OcrQaListStatus;
  className?: string;
}

/** Per-tier lifecycle status pill for QA list items (card / compact / table). */
export function OcrQaListStatusBadge({
  status,
  className
}: OcrQaListStatusBadgeProps) {
  const t = useTranslations('ocrQa');
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
  const Icon = style.icon;

  return (
    <Badge
      variant='outline'
      className={cn('gap-1 text-[10px]', style.className, className)}
    >
      <Icon className='h-3 w-3' />
      {t(`listStatus_${status}`)}
    </Badge>
  );
}
